import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSetGoogleAdsCustomerIdMutationOptions } from '@/lib/query-options';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';
import type { GoogleAdsAccount } from '@/lib/types';

/**
 * Make a Google Ads account the one the current organisation uses (#6668).
 *
 * The one owner of "select an account": persists the id on the membership, updates
 * the account and organisation atoms so every surface reflects it at once, and
 * invalidates the conversion actions and account users that depend on the account. Called from the
 * standalone picker (user menu, campaign pages) and from the Google Ads integration
 * modal, where connecting and choosing the account are one workflow. Throws on
 * failure so the caller can show the reason (a currency mismatch, say) in place.
 */
export function useSelectGoogleAdsAccount() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useSetGoogleAdsCustomerIdMutationOptions();
  const setGoogleAdsAccount = useSetAtom(googleAdsAccountAtom);
  const currentOrganization = useAtomValue(organizationDataAtom);
  const setOrganizationData = useSetAtom(organizationDataAtom);

  const selectAccount = useCallback(
    async (account: GoogleAdsAccount) => {
      await mutateAsync({
        googleAdsAccountId: account.googleads_account_id,
        organizationId: currentOrganization?.id,
      });

      setGoogleAdsAccount(account);

      if (currentOrganization) {
        setOrganizationData({
          ...currentOrganization,
          membership: {
            ...currentOrganization.membership,
            last_selected_googleads_account_name: account.name || account.googleads_account_id,
            last_selected_googleads_account_id: account.googleads_account_id,
          },
        });
      }

      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
      // The Providers tab and the invite modal list the selected account's users (#6827).
      queryClient.invalidateQueries({ queryKey: ['googleAdsAccountUsers'] });
    },
    [mutateAsync, currentOrganization, setGoogleAdsAccount, setOrganizationData, queryClient],
  );

  return { selectAccount, isPending };
}
